import React, { useEffect, useState, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Cookies from "js-cookie";
import host from "../../global.jsx";
// import { PieChart, Pie, Cell, Tooltip as RechartsTooltip } from "recharts";
import { MoveLeft, X, SlidersHorizontal, ChevronUp, ChevronDown, XCircleIcon } from "lucide-react";
import { Table, Thead, Tbody, Tr, Th, Td } from "react-super-responsive-table";
import "react-super-responsive-table/dist/SuperResponsiveTableStyle.css";
import AccLvlGraph from "./AccLvlGraph";
// import { BarChart } from "@mui/x-charts/BarChart";
// import { axisClasses } from "@mui/x-charts/ChartsAxis";
// import DateRangePicker from "../DatePicker";
import { fetchAllAccountLevelData, extendPlan, convertToPaid } from './services/accLvlService';
import { ExtendPlanModal, ConvertToPaidModal } from './PlanManagementModals';
import { toast } from "react-toastify";
import { FilterButton } from "./components/Filter/FilterButton";
import FilterPanel from "./components/Filter/FilterPanel";
// import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LabelList } from "recharts";
import ReusableTable from './components/ReusableTable/ReusableTable.jsx';
import StackedBarChart from "./components/Barchart/StackedBarChart.jsx";
import useClickOutside from "./hooks/useClickOutside";
import { formatDate } from "./utils/dateHelpers.js";

const AccLvlDash = () => {
  const { organisation } = useParams();
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [interviewData, setInterviewData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [jobPostingsData, setJobPostingsData] = useState({ org_admin_id: "", job_descriptions: [] });
  const [resumeParseData, setResumeParseData] = useState(null);
  // const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [tempDateRange, setTempDateRange] = useState([null, null]);
  const [dateRange, setDateRange] = useState([null, null]);
  const [selectedDatePeriod, setSelectedDatePeriod] = useState("custom");
  const [isDateDropdownOpen, setIsDateDropdownOpen] = useState(false);
  const [chartDateRange, setChartDateRange] = useState([null, null]);

  // Plan management (extend / convert to paid)
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [planActionSubmitting, setPlanActionSubmitting] = useState(false);

  const filterRef = useRef(null);
  const buttonRef = useRef(null);

  const visibleColumns = {
  name: true,
  email: true,
  lastLogin: true,
  interviews: true,
};

  // const formatDate = (dateString) => {
  //   return new Date(dateString).toLocaleDateString("en-US", {
  //     year: "numeric",
  //     month: "short",
  //     day: "numeric",
  //   });
  // };

  const toISTStart = (d) => {
    const ist = new Date(d.getTime() + 5.5 * 60 * 60 * 1000);
    ist.setHours(0, 0, 0, 0);
    return ist;
  };

  const toISTEnd = (d) => {
    const ist = new Date(d.getTime() + 5.5 * 60 * 60 * 1000);
    ist.setHours(23, 59, 59, 999);
    return ist;
  };

    /* ---------- 30-DAY SUMMARY ---------- */
  const chartData = useMemo(() => {
    if (!userData || !jobPostingsData || !resumeParseData || !interviewData) {
      return [];
    }

    const result = {};

    // ---------- ADMIN ----------
    const admin = userData?.org_admin_name || "Admin";
    result[admin] = { jobs: 0, resumes: 0, interviews: 0 };

    // Jobs
    jobPostingsData?.job_descriptions?.forEach((j) => {
      if (!j.postingDate || j.postingDate === "N/A") return;
      const d = toISTStart(new Date(j.postingDate));
      if (isDateInRange(d, chartDateRange)) result[admin].jobs++;
    });

    // Resumes (admin)
    resumeParseData?.org_admin_resume_parsing_dates?.forEach((e) => {
      if (!e?.date || !["created_date", "bulk_upload_date"].includes(e.type)) return;
      const d = toISTStart(new Date(e.date));
      if (isDateInRange(d, chartDateRange)) result[admin].resumes++;
    });

    // Interviews (admin)
    interviewData?.org_admin_invited_dates?.forEach((t) => {
      if (!t || t === "N/A") return;
      const d = toISTStart(new Date(t));
      if (isDateInRange(d, chartDateRange)) result[admin].interviews++;
    });

    // ---------- HRs ----------
    const hrs = userData?.hrs || [];
    hrs.forEach((hr) => {
      const name = hr.name;
      if (!name) return;
      result[name] = { jobs: 0, resumes: 0, interviews: 0 };

      // Resumes per HR
      const hrResume = resumeParseData?.hr_resume_parsing?.find((x) => x.name === name);
      hrResume?.resume_parsing_dates?.forEach((e) => {
        if (!e?.date || !["created_date", "bulk_upload_date"].includes(e.type)) return;
        const d = toISTStart(new Date(e.date));
        if (isDateInRange(d, chartDateRange)) result[name].resumes++;
      });

      // Interviews per HR
      const hrInterview = interviewData?.hr_usage?.find((x) => x.name === name);
      hrInterview?.invited_dates?.forEach((t) => {
        if (!t || t === "N/A") return;
        const d = toISTStart(new Date(t));
        if (isDateInRange(d, chartDateRange)) result[name].interviews++;
      });
    });

    // === CALCULATE TOTAL ACTIVITY ===
    const totalActivity = Object.values(result).reduce((sum, stats) => {
      return sum + stats.jobs + stats.resumes + stats.interviews;
    }, 0);

    // === ADD PERCENTAGE TO EACH USER ===
    const dataWithPercentage = Object.entries(result).map(([fullName, stats]) => {
  const userTotal = stats.jobs + stats.resumes + stats.interviews;
  const percentage = totalActivity > 0 ? Math.round((userTotal / totalActivity) * 100) : 0;

  // Create short name"
  const nameParts = fullName.trim().split(/\s+/);
    const shortName =
      nameParts.length >= 2
        ? `${nameParts[0]} ${nameParts[nameParts.length - 1][0]}`
        : nameParts[0];

    return {
      name: fullName,         
      shortName: shortName,    
      jobs: stats.jobs,
      resumes: stats.resumes,
      interviews: stats.interviews,
      percentage: percentage > 0 ? `${percentage}%` : '',
    };
  });

    return dataWithPercentage;
  }, [
    userData,
    jobPostingsData,
    resumeParseData,
    interviewData,
    chartDateRange,
  ]);

  function isDateInRange(d, range) {
    const [start, end] = range;
    if (!start && !end) return true;
    const ds = d.getTime();
    if (start && end) {
      return ds >= toISTStart(start).getTime() && ds <= toISTEnd(end).getTime();
    }
    if (start) return ds >= toISTStart(start).getTime();
    if (end) return ds <= toISTEnd(end).getTime();
    return true;
  }

    const applyFilter = () => {
    setDateRange(tempDateRange);
    setChartDateRange(tempDateRange);
    setIsFilterOpen(false);
  };

  const resetFilter = () => {
    setTempDateRange([null, null]);
    setDateRange([null, null]);
    setSelectedDatePeriod("custom");
    setChartDateRange([null, null]);
    setIsFilterOpen(false);
  };

  // const closeDetailModal = () => setDetailModalOpen(false);

  const chartSeries = useMemo(
    () => [
      {
        id: "jobs",
        label: "Jobs Created",
        dataKey: "jobs",
        stack: "total",
        color: "#ad3bdd", // Purple
      },
      {
        id: "resumes",
        label: "Resumes Parsed",
        dataKey: "resumes",
        stack: "total",
        color: "#B282F9", // Light purple
      },
      {
        id: "interviews",
        label: "Interviews Scheduled",
        dataKey: "interviews",
        stack: "total",
        color: "#6B46C1", // Dark purple
      },
    ],
    []
  );

  // const useClickOutside = (refs, handler) => {
  //   useEffect(() => {
  //     const listener = (event) => {
  //       if (refs.some(ref => ref.current && ref.current.contains(event.target))) {
  //         return;
  //       }
  //       handler();
  //     };
  //     document.addEventListener("mousedown", listener);
  //     return () => document.removeEventListener("mousedown", listener);
  //   }, [refs, handler]);
  // };

  // Use it
  useClickOutside([filterRef, buttonRef], () => {
    setIsFilterOpen(false);
    setIsDateDropdownOpen(false);
  });

  const activeFilters = [];

if (selectedDatePeriod !== "custom" || dateRange[0] || dateRange[1]) {
  activeFilters.push({
    label: `Date: ${
      selectedDatePeriod === "custom"
        ? dateRange[0]
          ? `${formatDate(dateRange[0])} - ${formatDate(dateRange[1])}`
          : "Custom"
        : selectedDatePeriod === "yesterday"
        ? "Yesterday"
        : selectedDatePeriod === "weekly"
        ? "Last 7 Days"
        : selectedDatePeriod === "monthly"
        ? "Last 30 Days"
        : selectedDatePeriod === "yearly"
        ? "Last Year"
        : "Custom"
    }`,
    onRemove: () => {
      setTempDateRange([null, null]);
      setDateRange([null, null]);
      setSelectedDatePeriod("custom");
      setChartDateRange([null, null]);
    },
  });
}

const filterCount = activeFilters.length;

  const handleBack = () => {
    navigate("/geniehireadmin/organisation");
  };

    const loadData = async ({ showLoader = true } = {}) => {
      if (!organisation) {
        setError("Organization ID is missing.");
        setLoading(false);
        return;
      }

      if (showLoader) setLoading(true);
      setError(null);

      try {
        const {
          userData,
          jobPostingsData,
          resumeParseData,
          interviewData,
        } = await fetchAllAccountLevelData(organisation);

        setUserData(userData);
        setJobPostingsData(jobPostingsData);
        setResumeParseData(resumeParseData);
        setInterviewData(interviewData);
      } catch (err) {
        console.error("Error loading account data:", err);
        if (showLoader) setError(err.message || "Something went wrong. Please try again.");
      } finally {
        if (showLoader) setLoading(false);
      }
    };

    useEffect(() => {
      loadData();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organisation]);

    const handleExtendPlan = async (payload) => {
      setPlanActionSubmitting(true);
      try {
        await extendPlan(organisation, payload);
        toast.success("Plan extended successfully");
        setShowExtendModal(false);
        await loadData({ showLoader: false });
      } catch (err) {
        toast.error(err?.response?.data?.detail || err.message || "Failed to extend plan");
      } finally {
        setPlanActionSubmitting(false);
      }
    };

    const handleConvertToPaid = async (payload) => {
      setPlanActionSubmitting(true);
      try {
        await convertToPaid(organisation, payload);
        toast.success("Account converted to Paid successfully");
        setShowConvertModal(false);
        await loadData({ showLoader: false });
      } catch (err) {
        toast.error(err?.response?.data?.detail || err.message || "Failed to convert account");
      } finally {
        setPlanActionSubmitting(false);
      }
    };

  const calculateHRStats = () => {
    if (!userData?.hrs) return { loggedIn: 0, assigned: 0 };
    const loggedIn = userData.hrs.filter((hr) => hr.last_login).length;
    const assigned = userData.hrs.length;
    return { loggedIn, assigned };
  };

  const hrStats = calculateHRStats();

  const formatDateWithoutSeconds = (dateString) => {
    if (!dateString) return "N/A";
    const dateStringClean = dateString.split(/\s|T/)[0];
    const utcDate = new Date(dateStringClean + "T00:00:00Z");
    if (isNaN(utcDate.getTime())) return "N/A";
    const istOffset = 5.5 * 60 * 60 * 1000;
    const istDate = new Date(utcDate.getTime() + istOffset);
    const day = String(istDate.getDate()).padStart(2, "0");
    const month = String(istDate.getMonth() + 1).padStart(2, "0");
    const year = istDate.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const formatEndDate = (dateString) => {
    if (!dateString) return "N/A";
    const datePart = dateString.split(/\s|T/)[0];
    const utcDate = new Date(datePart + "T00:00:00Z");
    if (isNaN(utcDate.getTime())) return "N/A";
    const istOffset = 5.5 * 60 * 60 * 1000;
    const istDate = new Date(utcDate.getTime() + istOffset);
    const day = String(istDate.getDate()).padStart(2, "0");
    const month = String(istDate.getMonth() + 1).padStart(2, "0");
    const year = istDate.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const processPieData = useMemo(() => {
    if (!userData?.hrs) return { data: [], total: 0 };

    const now = new Date();
    const nowIST = new Date(
      now.getTime() + now.getTimezoneOffset() * 60 * 1000 + 5.5 * 60 * 60 * 1000
    );
    let count0to7 = 0;
    let count8to15 = 0;
    let count16to30 = 0;
    let countNever = 0;

    userData.hrs.forEach((hr) => {
      if (hr.last_login && hr.last_login !== "N/A") {
        const lastLogin = new Date(hr.last_login);
        const lastLoginIST = new Date(
          lastLogin.getTime() + 5.5 * 60 * 60 * 1000
        );
        const diffTime = nowIST - lastLoginIST;
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        if (diffDays <= 7) {
          count0to7++;
        } else if (diffDays <= 15) {
          count8to15++;
        } else {
          count16to30++;
        }
      } else {
        countNever++;
      }
    });

    const used = count0to7 + count8to15 + count16to30 + countNever;
    const remaining = userData.total_hr_allocated - used;

    const data = [
      { name: "HR Assigned", value: used },
      { name: "HR Available", value: remaining > 0 ? remaining : 0 },
    ].filter((entry) => entry.value > 0);
    const total = userData.total_hr_allocated;
    return { data, total };
  }, [userData]);

  const tableData = useMemo(() => {
    if (!userData) {
      return [];
    }

  const data = [];

    // Add org admin row
    data.push({
      name: userData.org_admin_name || "N/A",
      email: userData.org_admin_email || "N/A",
      lastLogin: userData.last_login ? formatDateWithoutSeconds(userData.last_login) : "N/A",
      interviews: userData.org_admin_candidate_count || 0,
    });

    // Add HR rows
    if (userData?.hrs && userData.hrs.length > 0) {
      userData.hrs.forEach((hr) => {
        data.push({
          name: hr.name || "N/A",
          email: hr.hremail || "N/A",
          lastLogin: hr.last_login ? formatDateWithoutSeconds(hr.last_login) : "N/A",
          interviews: hr.candidate_count || 0,
        });
      });
    }

    return data;
  }, [userData]); // Dependency on userData

  const columns = [
    { key: 'name', label: 'Name', width: '25%' }, // Width optional; adjust for layout
    { key: 'email', label: 'Email Id', width: '30%' },
    { key: 'lastLogin', label: 'Last Login', width: '20%' },
    { key: 'interviews', label: 'Interviews Scheduled', width: '25%' },
  ];

  if (loading) {
    return (
      <div className="p-6 text-white">
        {/* Skeleton for Header */}
        <div className="w-screen-full glass-effect py-4 px-0 mb-6 -mx-14 -ml-14">
          <div className="max-w-7xl ml-14 flex flex-wrap items-center justify-between">
            <div className="flex flex-col items-start space-y-2 text-sm">
              <div className="flex items-center space-x-4">
                <div className="w-5 h-5 bg-gray-600 rounded animate-pulse"></div>
                <div className="w-48 h-6 bg-gray-600 rounded animate-pulse"></div>
              </div>
              <div className="flex flex-wrap items-center space-x-14 text-sm">
                <div className="w-24 h-4 bg-gray-600 rounded animate-pulse"></div>
                <div className="w-24 h-4 bg-gray-600 rounded animate-pulse"></div>
                <div className="w-24 h-4 bg-gray-600 rounded animate-pulse"></div>
              </div>
            </div>
          </div>
        </div>

        {/* Skeleton for Graphs and Pie Chart */}
        <div className="flex flex-col gap-8">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Skeleton for AccLvlGraph */}
            <div
              className="glass-effect p-4 rounded-2xl border border-transparent"
              style={{ width: "100%", maxWidth: "570px", height: "353px" }}
            >
              <div className="w-32 h-4 bg-gray-600 rounded animate-pulse mb-4"></div>
              <div className="w-full h-64 bg-gray-600 rounded animate-pulse"></div>
            </div>
            {/* Skeleton for HR Allocation */}
            <div
              className="glass-effect p-4 rounded-2xl border border-transparent"
              style={{ width: "100%", maxWidth: "570px", height: "353px" }}
            >
              <div className="w-32 h-4 bg-gray-600 rounded animate-pulse mb-4"></div>
              <div className="flex items-start">
                <div className="w-48 h-48 bg-gray-600 rounded-full animate-pulse mr-8"></div>
                <div className="flex flex-col space-y-4">
                  <div className="w-40 h-6 bg-gray-600 rounded animate-pulse"></div>
                  <div className="w-20 h-8 bg-gray-600 rounded animate-pulse"></div>
                  <div className="w-32 h-4 bg-gray-600 rounded animate-pulse"></div>
                  <div className="w-32 h-4 bg-gray-600 rounded animate-pulse"></div>
                </div>
              </div>
            </div>
          </div>

          {/* Skeleton for Account Users Table */}
          <div
            className="glass-effect p-4 rounded-2xl border border-transparent"
            style={{ height: "353px" }}
          >
            <div className="w-32 h-4 bg-gray-600 rounded animate-pulse mb-4"></div>
            <table className="w-full">
              <thead>
                <tr className="border-t border-b border-gray-700">
                  <th className="text-left py-3 px-4">
                    <div className="w-20 h-4 bg-gray-600 rounded animate-pulse"></div>
                  </th>
                  <th className="text-left py-3 px-4">
                    <div className="w-20 h-4 bg-gray-600 rounded animate-pulse"></div>
                  </th>
                  <th className="text-left py-3 px-4">
                    <div className="w-20 h-4 bg-gray-600 rounded animate-pulse"></div>
                  </th>
                  <th className="text-left py-3 px-4">
                    <div className="w-20 h-4 bg-gray-600 rounded animate-pulse"></div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {[...Array(3)].map((_, index) => (
                  <tr key={index} className="border-b border-gray-700/50">
                    <td className="py-3 px-4">
                      <div className="w-32 h-4 bg-gray-600 rounded animate-pulse"></div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="w-48 h-4 bg-gray-600 rounded animate-pulse"></div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="w-24 h-4 bg-gray-600 rounded animate-pulse"></div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="w-16 h-4 bg-gray-600 rounded animate-pulse"></div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  if (error) return <p className="text-white p-6">{error}</p>;

  return (
    <div className="p-6 text-white">
      <div
        className="w-screen-full glass-effect py-4 px-0 mb-6 -mx-14 -ml-14"
        style={{
          background:
            "linear-gradient(252.82deg, rgba(217, 217, 217, 0.025) 25.41%, rgba(111, 105, 197, 0.01) 115.2%)",
        }}
      >
        <div className="ml-14 mr-14 flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-col items-start space-y-2 text-sm">
            <div className="flex items-center space-x-4">
              <button
                onClick={handleBack}
                className="flex items-center text-gray-400 hover:text-white transition-colors p-1 -ml-2"
                title="Go back"
              >
                <MoveLeft className="w-5 h-5 text-[#C9A3FF]" />
              </button>
              <h1 className="text-2xl font-normal text-white py-2 pt-1">
                {userData.org_name || "N/A"}
              </h1>
            </div>
            <div className="flex flex-wrap items-center space-x-14 text-sm">
              <span>
                <span style={{ color: "#D3B4FF" }}>Account Type - </span>
                <span
                  className={`px-2 py-0 rounded-full text-sm font-medium ${
                    userData.account_type === "Trial"
                      ? "bg-blue-600 text-white"
                      : "bg-green-600 text-white"
                  }`}
                >
                  {userData.account_type || "N/A"}
                </span>
              </span>
              <span>
                <span style={{ color: "#D3B4FF" }}>End Date - </span>
                <span className="font-semibold" style={{ color: "white" }}>
                  {formatEndDate(userData.end_date)}
                </span>
              </span>
              {/* Total Interviews */}
              <span>
                <span style={{ color: "#D3B4FF" }}>Total Interviews - </span>
                <span className="font-semibold" style={{ color: "white" }}>
                  {userData.grand_total_interviews_count || "N/A"}
                </span>
              </span>

              {/* HR Allocated */}
              <span>
                <span style={{ color: "#D3B4FF" }}>HR Allocated - </span>
                <span className="font-semibold" style={{ color: "white" }}>
                  {userData.total_hr_allocated || "N/A"}
                </span>
              </span>

              {/* HR Assigned */}
              <span>
                <span style={{ color: "#D3B4FF" }}>HR Assigned - </span>
                <span className="font-semibold" style={{ color: "white" }}>
                  {userData.hrs?.length || 0}
                </span>
              </span>

              {/* HR Available */}
              <span>
                <span style={{ color: "#D3B4FF" }}>HR Available - </span>
                <span className="font-semibold" style={{ color: "white" }}>
                  {Math.max(0, (userData.total_hr_allocated || 0) - (userData.hrs?.length || 0))}
                </span>
              </span>
            </div>
          </div>

          {/* Plan management actions */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowExtendModal(true)}
              className="px-6 py-2 border border-[#D884FE] text-[#C9A3FF] rounded-full hover:bg-purple-900/30 transition-colors text-sm font-medium"
            >
              Extend Now
            </button>
            {userData.account_type !== "Paid" && (
              <button
                onClick={() => setShowConvertModal(true)}
                className="px-6 py-2 bg-gradient-to-r from-pink-500 to-blue-500 text-white rounded-full hover:opacity-90 transition-opacity text-sm font-medium"
              >
                Convert to Paid
              </button>
            )}
          </div>
        </div>
      </div>

      <ExtendPlanModal
        isOpen={showExtendModal}
        onClose={() => setShowExtendModal(false)}
        subscription={userData.subscription}
        onSubmit={handleExtendPlan}
        submitting={planActionSubmitting}
      />
      <ConvertToPaidModal
        isOpen={showConvertModal}
        onClose={() => setShowConvertModal(false)}
        subscription={userData.subscription}
        onSubmit={handleConvertToPaid}
        submitting={planActionSubmitting}
      />

      <div className="flex flex-col gap-8">
        <div className="flex flex-col lg:flex-row gap-8">
          <AccLvlGraph userData={userData} interviewData={interviewData} jobPostingsData={jobPostingsData} resumeParseData={resumeParseData} />

          {/* HR Allocation Card */}
          <div
            className="glass-effect p-4 rounded-2xl border border-transparent"
            style={{
              width: "100%",
              maxWidth: "570px",
              height: "353px",
              borderRadius: "10px",
              background:
                "linear-gradient(252.82deg, rgba(217, 217, 217, 0.025) 25.41%, rgba(115, 108, 207, 0.005) 115.2%)",
              backdropFilter: "blur(75px)",
              position: "relative",
            }}
          >
            <div className="flex justify-between items-center">
              <h3
                className="text-lg font-medium mb-8"
                style={{
                  color: "#D3B4FF",
                  fontFamily: "Inter",
                  fontWeight: 300,
                  fontStyle: "Light",
                  fontSize: "16px",
                  lineHeight: "100%",
                  letterSpacing: "1%",
                  opacity: 1,
                  transform: "rotate(0deg)",
                  whiteSpace: "nowrap",
                  marginTop: "7px",
                  marginLeft: "10px",
                }}
              >
                HR Utilization
              </h3>

              <div className="relative -mt-8">  {/* Wrap for positioning; keeps the -mt-8 from original button */}
                <FilterButton
                  filterCount={filterCount}
                  onClick={() => setIsFilterOpen(!isFilterOpen)}
                  onClear={() => {
                    setTempDateRange([null, null]);
                    setDateRange([null, null]);
                    setSelectedDatePeriod("custom");
                    setChartDateRange([null, null]);  // Add this to immediately apply the clear
                  }}
                />

                <FilterPanel
                  ref={filterRef}
                  isOpen={isFilterOpen}
                  onClose={() => setIsFilterOpen(false)}
                  activeFilters={activeFilters}
                  selectedPeriod={selectedDatePeriod}
                  onPeriodChange={setSelectedDatePeriod}
                  tempDateRange={tempDateRange}
                  onTempDateChange={setTempDateRange}
                  onApply={applyFilter}
                  onReset={resetFilter}
                  showCustomPicker={true}  // Default is true; matches your original
                  position="bottom-right"  // Matches your absolute positioning (top-full mt-2 right-0)
                  dropdownOpen={isDateDropdownOpen}
                  setDropdownOpen={setIsDateDropdownOpen}
                />
              </div>
              
            </div>
            <div
              className="rounded-xl p-1 mb-4 flex items-start"
              style={{ color: "white" }}
            >
              <div className="rounded-xl p-1 px-5 w-[540px] h-[300px] -mt-8 cursor-pointer">
              {/* Invisible Horizontal Scroll Container */}
              <div 
                className="overflow-x-auto overflow-y-hidden"
                style={{
                  scrollbarWidth: 'none', /* Firefox */
                  msOverflowStyle: 'none', /* IE/Edge */
                }}
              >
                <div 
                  className="min-w-max"
                  style={{ WebkitOverflowScrolling: 'touch' }}
                >
                  {/* Hide scrollbar in WebKit browsers */}
                  <style jsx>{`
                    div::-webkit-scrollbar {
                      display: none;
                    }
                  `}</style>

                  <div className="overflow-x-auto -mx-8 px-4 mt-0 my-28">
                    <div style={{ minWidth: Math.max(600, chartData.length * 40) }}>
                      <StackedBarChart data={chartData} height={450} />

                    </div>
                  </div>
                </div>
              </div>

              {/* CUSTOM LEGEND - Centered Below */}
              <div className="flex justify-center gap-4 -mt-80">
                {[
                  { label: "Jobs Created",          color: "#ad3bdd" },
                  { label: "Resumes Parsed",       color: "#B282F9" },
                  { label: "Interviews Scheduled", color: "#6B46C1" },
                ].map((item) => (
                  <div key={item.label} className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: item.color }}
                    />
                    <span className="text-sm text-white font-light">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
            </div>
          </div>
        </div>

        {/* Account Users Card */}
        <ReusableTable
          columns={columns.filter(col => col != null)}
          data={tableData}
          visibleColumns={visibleColumns}
          noDataMessage="No HRs associated with this organization"
          truncateLength={50}
          rowClassName="cursor-pointer"
        />
      </div>
    </div>
  );
};

export default AccLvlDash;